import { FC, memo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { USER_ACCESS_TOKEN } from '@constants/auth';
import { ROUTING } from '@constants/routing';
import { RootState } from '@store/store';
import { prefetchInbox } from '@utils/inboxCache';

const PrivateRoutes: FC = () => {
  const navigate = useNavigate();
  const authState = useSelector((state: RootState) => state.auth);
  const hasPrefetched = useRef(false);

  // Development mode bypass for testing
  const isDevelopmentMode = import.meta.env.DEV;
  const hasMockAuth = typeof window !== 'undefined' && (window as any).__MOCK_AUTH__;

  useEffect(() => {
    if (authState.isConfirmed === false && !hasMockAuth) {
      navigate(`/${ROUTING.CONFIRM_EMAIL}`, { replace: true });
    }
  }, [navigate, authState.isConfirmed, hasMockAuth]);

  // Pre-fetch inbox data on login for instant mailbox loading
  useEffect(() => {
    if (authState.isAuthenticated && !hasPrefetched.current) {
      hasPrefetched.current = true;
      prefetchInbox();
    }
  }, [authState.isAuthenticated]);

  // Allow bypass in development with mock auth or when access token exists in localStorage
  if (!authState.isAuthenticated && !hasMockAuth) {
    // In development, check for token in localStorage as fallback
    if (isDevelopmentMode && typeof window !== 'undefined') {
      const hasTokenInStorage = localStorage.getItem(USER_ACCESS_TOKEN);
      if (!hasTokenInStorage) {
        return <Navigate to={ROUTING.LOGIN} replace />;
      }
    } else {
      return <Navigate to={ROUTING.LOGIN} replace />;
    }
  }

  return <Outlet />;
};

export default memo(PrivateRoutes);
